import { Injectable } from '@angular/core'
import { Platform } from '@ionic/angular'
import { initializeApp } from 'firebase/app'
import snake_case from 'lodash.snakecase'
import {
  AppUpdateLoggingParams,
  ClickLoggingParams,
  EventLoggingParams,
  ExternalLinkLoggingParams,
  HttpLoggingParams,
  LogEvent,
  LogEventKey,
  LogState,
  LogType,
  RouterLoggingParams,
} from 'src/app/interfaces/log'
import { FeedCard } from 'src/app/interfaces/main'
import { PushCode, PushData } from 'src/app/interfaces/notification'
import { environment } from 'src/environments/environment'
import { customLocalStorage } from 'src/utils/utils'
import packageInfo from '../../../../package.json'
import { TestGroup } from '../user/user.service'
import { User } from './../../interfaces/user'
import { AdLogService } from './ad/ad-log.service'
import { Amplitude } from './amplitude'
import { AskLogService } from './ask/ask-log.service'
import { AttachmentTypeLogService } from './attachment-type/attachment-type-log.service'
import { MbtiBestFriendLogService } from './best-friend/mbti-best-friend-log.service'
import { CabinetLogService } from './cabinet/cabinet-log.service'
import { ChatAnalysisLogService } from './chat-analysis/chat-analysis-log.service'
import { DailyLogLogService } from './daily-log/daily-log.service'
import { EgenTetoQuizLogService } from './egen-teto/egen-teto-quiz-log.service'
import { ExperimentService, TEST_GROUP_KEY, getTestGroup, testGroupMap } from './experiment/experiment.service'
import { FavourTournamentLogService } from './favour-tournament/favour-tournament-log.service'
import { GroupChemiLogService } from './group-chemi/group-chemi-log.service'
import { GuessMeLogService } from './guess-me/guess-me-log.service'
import { HspQuizLogService } from './hsp-quiz/hsp-quiz-log.service'
import { IdealTypeQuizLogService } from './ideal-type-quiz/ideal-type-quiz-log.service'
import { IdealTypeLogService } from './ideal-type/ideal-type-log.service'
import { ImageGameLogService } from './image-game/image-game-log.service'
import { DailyLogQuizLogService } from './log-quiz/daily-log-quiz-log.service'
import { SajuLogService } from './saju/saju-log.service'
import { SchoolIdealTypeLogService } from './school-ideal-type/school-ideal-type-log.service'
import { SignalLogService } from './signal/signal-log.service'
import { SimpleSoulmateLogService } from './simple-soulmate/simple-soulmate-log.service'
import { SurveyLogService } from './survey/survey-log.service'
import { ThinkingChairLogService } from './thinking-chair/thinking-chair-log.service'

/**
 * 세션 체크 로직
 *  1. 앱 실행 & 포어그라운드 전환시 현재 GA 세션 id 확인
 *  2. 로컬 스토리지에 기록된 마지막 세션 id와 비교
 *  3. 마지막 세션 id와 현재 세션 id가 일치하지 않으면 새 세션으로 인식
 */
type SessionData = {
  sessionId: number
  createdAt: number
}

const LoggingSessionKey = 'logging_session'
const PromotionPushKey = 'promotion_push'

@Injectable({
  providedIn: 'root',
})
export class LogService {
  constructor(private platform: Platform) {}

  private experimentService = new ExperimentService(this)
  private askLogService = new AskLogService(this)
  private surveyLogService = new SurveyLogService(this)
  private attachmentTypeLogService = new AttachmentTypeLogService(this)
  private signalLogService = new SignalLogService(this)
  private tcLogService = new ThinkingChairLogService(this)
  private imageGameLogService = new ImageGameLogService(this)
  private guessMeLogService = new GuessMeLogService(this)
  private cabinetLogService = new CabinetLogService(this)
  private favourTournamentLogService = new FavourTournamentLogService(this)
  private adLogService = new AdLogService(this)
  private idealTypeLogService = new IdealTypeLogService(this)
  private mbtiBestFriendLogService = new MbtiBestFriendLogService(this)
  private groupChemiLogService = new GroupChemiLogService(this)
  private idealTypeQuizLogService = new IdealTypeQuizLogService(this)
  private schoolIdealTypeLogService = new SchoolIdealTypeLogService(this)
  private logQuizLogService = new DailyLogQuizLogService(this)
  private dailyLogLogService = new DailyLogLogService(this)
  private chatAnalysisLogService = new ChatAnalysisLogService(this)
  private hspQuizLogService = new HspQuizLogService(this)
  private egenTetoQuizLogService = new EgenTetoQuizLogService(this)
  private sajuLogService = new SajuLogService(this)
  private simpleSoulmateLogService = new SimpleSoulmateLogService(this)

  exp: ExperimentService = this.experimentService
  ask: AskLogService = this.askLogService
  survey: SurveyLogService = this.surveyLogService
  attachment: AttachmentTypeLogService = this.attachmentTypeLogService
  tc: ThinkingChairLogService = this.tcLogService
  signal: SignalLogService = this.signalLogService
  imageGame: ImageGameLogService = this.imageGameLogService
  guessMe: GuessMeLogService = this.guessMeLogService
  cabinet: CabinetLogService = this.cabinetLogService
  ft: FavourTournamentLogService = this.favourTournamentLogService
  ad: AdLogService = this.adLogService
  idealType: IdealTypeLogService = this.idealTypeLogService
  idealTypeQuiz: IdealTypeQuizLogService = this.idealTypeQuizLogService
  mbtiBF: MbtiBestFriendLogService = this.mbtiBestFriendLogService
  groupChemi: GroupChemiLogService = this.groupChemiLogService
  schoolIdealType: SchoolIdealTypeLogService = this.schoolIdealTypeLogService
  logQuiz: DailyLogQuizLogService = this.logQuizLogService
  dailyLog: DailyLogLogService = this.dailyLogLogService
  ca: ChatAnalysisLogService = this.chatAnalysisLogService
  hspQuiz: HspQuizLogService = this.hspQuizLogService
  egenTetoQuiz: EgenTetoQuizLogService = this.egenTetoQuizLogService
  saju: SajuLogService = this.sajuLogService
  simpleSoulmate: SimpleSoulmateLogService = this.simpleSoulmateLogService

  utms: {
    utmCampaign?: string
    utmMedium?: string
    utmSource?: string
  } = {}

  private logState: LogState = {}
  private configLoggingTimeout: any | undefined
  private webConfig: { [key: string]: string } = {}
  private amplitude = new Amplitude()
  private superProperties: Record<string, string> = {}

  async init(): Promise<void> {
    this.addGoogleAnalyticsScript()
    this.amplitude = new Amplitude()
    await this.amplitude.init()
    const firebaseApp = initializeApp({
      apiKey: 'AIzaSyDUIwnnniLKNPGD6pLXTMwhfZ4e6dYqOf8',
      authDomain: 'types-ecea9.firebaseapp.com',
      projectId: 'types-ecea9',
      storageBucket: 'types-ecea9.appspot.com',
      messagingSenderId: '759544068391',
      appId: '1:759544068391:web:bd0e7a2548cf9e60015102',
      measurementId: 'G-P9262YYWFJ',
    })
    this.exp.initRemoteConfig(firebaseApp)
    // this.remoteConfig.settings.minimumFetchIntervalMillis = 0;
    this.setInitialSuperProperties()
  }

  private setInitialSuperProperties(): void {
    this.setSuperProperty('version_self_defined', packageInfo.version)
    this.setSuperProperty('platform', 'web')
    this.setSuperProperty('screen_resolution', `${window.innerWidth}x${window.innerHeight}`)
    this.setSuperProperty('device_category', JSON.stringify(this.platform.platforms()))
    this.setSuperProperty('is_logged_in', localStorage.getItem('jwt') ? 'true' : 'false')
  }

  setSuperProperty(key: string, value: string): void {
    this.superProperties[key] = value
  }

  /** 앱 시작시 + logging action마다 세션 세팅
   * 만약 30분 지났다면 초기화
   */
  setSession(): void {
    const currentSession = this.getCurrentSession()
    if (currentSession) {
      // 30분 안지났을 경우 시간 연장 후 return (시간 연장만 하기)
      const diffSeconds = (new Date().getTime() - currentSession.createdAt) / 1000
      if (diffSeconds <= 30 * 60) {
        this.extendSession()
        this.setPushDataToConfig()
        return
      }
    }
    this.setNewSession()
  }

  /**
   * 세션 초기화
   *  로컬스토리지의 푸시 데이터 제거
   *  새로운 세션 아이디로 세팅
   */
  private setNewSession(): void {
    this.clearPromotionPush()
    const newSessionId = Math.floor(Math.random() * 1000000)
    customLocalStorage.setItem(
      LoggingSessionKey,
      JSON.stringify({
        sessionId: newSessionId,
        createdAt: new Date().getTime(),
      })
    )
  }

  /** 세션 데이터 가져오기 */
  getCurrentSession(): SessionData | undefined {
    const sessionData = customLocalStorage.getItem(LoggingSessionKey)
    if (!sessionData) {
      return
    }
    return JSON.parse(sessionData)
  }

  /** 세션 시간 연장 */
  private extendSession(): void {
    const currentSession = this.getCurrentSession()
    if (!currentSession) {
      return
    }

    customLocalStorage.setItem(
      LoggingSessionKey,
      JSON.stringify({
        sessionId: currentSession.sessionId,
        createdAt: new Date().getTime(),
      })
    )
  }

  /** promotion push 데이터 제거 */
  clearPromotionPush(): void {
    customLocalStorage.removeItem(PromotionPushKey)
  }

  /** promotion push만 추가 */
  setPromotionPush(newPush: PushData): void {
    if (newPush.code !== PushCode.PROMOTION) {
      return
    }
    customLocalStorage.setItem(PromotionPushKey, JSON.stringify(newPush))
  }

  getCurrentPromotionPush(): PushData | undefined {
    const push = customLocalStorage.getItem(PromotionPushKey)
    if (push) {
      return JSON.parse(push)
    }
  }

  /** 푸시 정보를 user config에 세팅 */
  setPushDataToConfig(): void {
    const currentPush = this.getCurrentPromotionPush()
    if (currentPush) {
      this.setUserConfig({
        key: LogType.PROMOTION_PUSH,
        value: currentPush.key ?? '',
      })
    }
  }

  addGoogleAnalyticsScript(): void {
    const gTagManagerScript = document.createElement('script')
    gTagManagerScript.async = true
    gTagManagerScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsMeasurementId}`
    document.head.appendChild(gTagManagerScript)

    // register google analytics
    const gaScript = document.createElement('script')
    gaScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag() { dataLayer.push(arguments); }
      gtag('js', new Date());
      gtag('config', '${environment.googleAnalyticsMeasurementId}');
    `
    document.head.appendChild(gaScript)
  }

  setLogState(data: LogState): void {
    this.logState = { ...this.logState, ...data }
  }

  clearLogState(): void {
    this.logState = {}
  }

  setUserId(user: User): void {
    if (user?.id !== undefined && user?.id !== null) {
      this.setUserConfig({ key: 'user_id', value: `${user.id}` })
      this.amplitude.setUserId(user.id ? '' + user.id : null)
    }
  }

  allTestGroups(): TestGroup[] {
    const result: TestGroup[] = []
    const enumValues: string[] = Object.values(TEST_GROUP_KEY)
    enumValues.forEach((testGroupKey: string) => {
      const testGroup = customLocalStorage.getItem(testGroupKey)
      if (testGroup) {
        result.push({ key: testGroupKey, value: testGroup })
      }
    })
    return result
  }

  setUserConfig(data?: Record<string, any>): void {
    const testGroups: { key: string; value: string }[] = this.allTestGroups()
    let properties = { ...data }
    if (testGroups.length > 0) {
      const testGroupProps: Record<string, string> = {}
      testGroups.forEach((testGroup) => {
        testGroupProps[testGroup.key] = testGroup.value
      })
      properties = {
        ...data,
        ...testGroupProps,
      }

      // add test group key
      Object.keys(properties).forEach((key: any) => {
        const testGroupKeys = testGroupMap.map((item) => item.queryParamKey)
        if (testGroupKeys.includes(key)) {
          const value = properties[key]
          const newKey = getTestGroup(key)
          if (newKey) {
            properties[newKey.logKey] = value
            delete properties[key]
            this.setSuperProperty(newKey.logKey, value)
          }
        }
      })
    }

    // to snake case
    const newParams: Record<string, string> = {}

    Object.keys(properties).forEach((key) => {
      newParams[snake_case(key)] = properties![key]
    })

    this.webConfig = {
      ...this.webConfig,
      ...newParams,
    }
    if (this.configLoggingTimeout !== undefined) {
      clearTimeout(this.configLoggingTimeout)
      this.configLoggingTimeout = undefined
    }
    this.configLoggingTimeout = setTimeout(() => {
      ;(window as any)?.gtag('config', environment.googleAnalyticsMeasurementId, { ...this.webConfig })
    }, 500)
    const adaptedProperties = this.userConfigKeyAdaptor(this.webConfig)
    this.amplitude.setUserProperties(adaptedProperties)
  }

  /** ga key -> amplitude key */
  private userConfigKeyAdaptor(data: Record<string, string>): Record<string, string> {
    const keys = [
      { ga: 'unique_name', others: 'Unique Name' },
      { ga: 'gender', others: 'Gender' },
      { ga: 'birth_year', others: 'Birth Year' },
      { ga: 'phone_number', others: 'Phone Number' },
      { ga: 'email', others: 'Email' },
      { ga: 'last_app_opened_at', others: 'Last App Opened At' },
      { ga: 'mbti_first_complete_at', others: 'MBTI First Complete At' },
      { ga: 'mbti_last_complete_at', others: 'MBTI Last Complete At' },
      { ga: 'signed_up_at', others: 'Signed Up At' },
      { ga: 'user_group', others: 'User Group' },
      { ga: 'mbti_answer_got_count', others: 'MBTI Answer Got' },
      { ga: 'user_mbti_type', others: 'MBTI Type' },
      { ga: 'survey_first_answered_at', others: 'Survey First Answered At' },
      { ga: 'survey_last_answered_at', others: 'Survey Last Answered At' },
      { ga: 'user_attachment_type', others: 'Attachment Type' },
      { ga: 'attachment_first_complete_at', others: 'Attachment First Complete At' },
      { ga: 'attachment_last_complete_at', others: 'Attachment Last Complete At' },
    ]
    const newData = { ...data }
    Object.keys(data).forEach((key) => {
      let compatibleKey = ''
      keys.forEach((keyData) => {
        if (keyData.ga === key) {
          compatibleKey = keyData.others
        }
      })
      if (compatibleKey) {
        newData[compatibleKey] = data[key]
        delete newData[key]
      }
    })
    return newData
  }

  async clearUserProperties(): Promise<void> {
    this.amplitude.clearUserProperties()
  }

  private setUserProperty(name: string, value: string): void {
    this.webConfig[name] = value
    if ((window as any)?.gtag) {
      ;(window as any).gtag('config', environment.googleAnalyticsMeasurementId, { ...this.webConfig })
    }
  }

  /** 메인 > 각 카드별 노출 수 */
  mainFeedExposeLogging(params: FeedCard, index: number): void {
    const { title, subtitle, moveTo } = params
    this.eventLogging(LogEvent.MAIN_FEED_EXPOSE, {
      order: index,
      title,
      subtitle,
      moveTo,
    })
  }

  routerLogging(params: RouterLoggingParams): void {
    this.eventLogging(LogEvent.ROUTE, params)
  }

  clickLogging(params: ClickLoggingParams): void {
    this.eventLogging(LogEvent.CLICK, params)
  }

  httpLogging(params: HttpLoggingParams): void {
    this.eventLogging(LogEvent.HTTP, params)
  }

  externalLinkLogging(params: ExternalLinkLoggingParams): void {
    this.eventLogging(LogEvent.EXTERNAL_LINK, params)
  }

  appUpdateLogging(params: AppUpdateLoggingParams): void {
    this.eventLogging(LogEvent.APP_UPDATE, params)
  }

  pushPermissionToggleLogging(params: { toggleType: 'dialog' | 'manual'; direction: 'on' | 'off' }): void {
    this.eventLogging(LogEvent.PUSH_PERMISSION_TOGGLE, params)
  }

  resultSaveButtonClickedLogging(): void {
    this.eventLogging(LogEvent.RESULT_SAVE_BUTTON_CLICKED)
  }

  resultSaveImpressionLogging(): void {
    this.eventLogging(LogEvent.RESULT_SAVE_IMPRESSION)
  }

  resultSaveModalButtonClickedLogging(): void {
    this.eventLogging(LogEvent.RESULT_SAVE_MODAL_BUTTON_CLICKED)
  }

  resultSaveModalImpressionLogging(): void {
    this.eventLogging(LogEvent.RESULT_SAVE_MODAL_IMPRESSION)
  }

  qrCodeModalViewed(type: string): void {
    this.eventLogging(LogEvent.QR_CODE_MODAL_VIEWED, { type })
  }

  eventLogging(logType: LogEventKey, params?: EventLoggingParams): void {
    params = { ...this.superProperties, ...params }
    if (params !== undefined) {
      Object.keys(params).forEach((key) => {
        if (params![key] instanceof FormData) {
          let convertedJson = {}
          for (const [formDataKey, formDataValue] of (params![key] as any).entries()) {
            convertedJson[formDataKey] = formDataValue
          }
          params![key] = JSON.stringify(convertedJson)
          return
        }
        if (typeof params![key] === 'object') {
          params![key] = JSON.stringify(params![key])
        }
      })
    }

    // to snake case
    const newParams: Record<string, string | boolean | number> = {}
    Object.keys(params).forEach((key) => {
      newParams[snake_case(key)] = params![key]
    })

    if (logType?.ga) {
      if (window && (window as any)?.gtag) {
        ;(window as any).gtag('event', logType, newParams)
      }
    }
    if (logType.amp) {
      if (!environment.production) {
        console.log('========== ', logType.amp, ' ==========')
      }
      // category, purpose - only for ga
      delete newParams.category
      delete newParams.purpose
      this.amplitude.logEvent(logType.amp, newParams)
    }
    // 이벤트 발생시마다 session 시간 연장. 만약 30분 지났다면 초기화
    this.setSession()
  }

  setUtms(path: string): void {
    const [url, queryParamString] = path.split('?')
    if (queryParamString !== undefined) {
      const queryParams: { [key: string]: string } = {}
      const splittedQueryParams = queryParamString.split('&')
      splittedQueryParams.forEach((param) => {
        const [key, value] = param.split('=')
        queryParams[key] = value
      })
      if (queryParams.utm_campaign) {
        this.utms.utmCampaign = queryParams.utm_campaign
        this.setUserProperty('utm_campaign', queryParams.utm_campaign)
        this.setSuperProperty('utm_campaign', queryParams.utm_campaign)
      }
      if (queryParams.utm_medium) {
        this.utms.utmMedium = queryParams.utm_medium
        this.setUserProperty('utm_medium', queryParams.utm_medium)
        this.setSuperProperty('utm_medium', queryParams.utm_medium)
      }
      if (queryParams.utm_source) {
        this.utms.utmSource = queryParams.utm_source
        this.setUserProperty('utm_source', queryParams.utm_source)
        this.setSuperProperty('utm_source', queryParams.utm_source)
      }
    }
  }
}
